import React from "react";
import { motion } from "framer-motion";
import { StoreButton, ApkButton } from "./DownloadButtons";
import { HERO } from "@/constants/plantusTestIds";
import { ShieldCheck, MapPinSimpleArea, CloudArrowUp } from "@phosphor-icons/react";

const HERO_MOCKUP = "/screenshots/hero_login.jpg";

const stats = [
  { Icon: ShieldCheck, label: "DSGVO-konform" },
  { Icon: MapPinSimpleArea, label: "GPS-genau" },
  { Icon: CloudArrowUp, label: "Cloud-Sync" },
];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Topographic grain background */}
      <div className="absolute inset-0 bg-topo opacity-40 pointer-events-none" />
      {/* Soft forest glow */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-forest-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] bg-terracotta/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left: text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-forest-50 border border-forest-100 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-forest-500 animate-pulse" />
            <span
              className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-forest-700"
              data-testid="hero-overline"
            >
              PSM-Dokumentation · EU 2023/564
            </span>
          </div>

          <h1
            data-testid={HERO.heading}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-forest-900"
          >
            Pflanzenschutz.
            <br />
            <span className="text-forest-600">Einfach.</span>{" "}
            <span className="relative inline-block">
              Rechtssicher.
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                aria-hidden
              >
                <path
                  d="M2 9 C 60 2, 140 14, 198 6"
                  stroke="#D97743"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </h1>

          <p
            data-testid={HERO.subtitle}
            className="mt-7 font-body text-base sm:text-lg leading-relaxed text-[#4A524C] max-w-xl"
          >
            <strong className="text-forest-700">Einfach. Schnell. Unkompliziert.</strong>{" "}
            Wenige Klicks, automatische Echtzeit Aufzeichnung, eine PSM
            Anwendung ist in Sekunden vor Ort dokumentiert. Plantus
            erleichtert Ihren Arbeitsalltag spürbar und der Dokumentationsumfang
            entspricht der gesetzlichen Mindestanforderung inklusive der
            ab 01.01.2026 geltenden Erweiterungen der
            EU-Durchführungsverordnung (EU)&nbsp;2023/564.
          </p>

          <div id="download" className="mt-9">
            <ApkButton testId={HERO.ctaApk} />
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            <StoreButton platform="ios" testId={HERO.ctaAppStore} />
            <StoreButton platform="android" testId={HERO.ctaPlayStore} />
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
            {stats.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 text-sm font-medium text-[#4A524C]"
              >
                <Icon size={18} weight="duotone" className="text-forest-600" />
                {label}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: floating mockup */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
          className="lg:col-span-5 relative flex justify-center"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-forest-100/60 to-transparent rounded-[3rem] blur-3xl" />
          <motion.div
            data-testid={HERO.mockup}
            className="relative"
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="relative mx-auto w-72 sm:w-80 lg:w-96 aspect-[9/19] rounded-[3rem] bg-gradient-to-br from-forest-700 to-forest-900 p-[10px] shadow-[0_40px_80px_-20px_rgba(20,30,20,0.45)]">
              <img
                src={HERO_MOCKUP}
                alt="Plantus App, Login & Funktionsübersicht"
                className="w-full h-full object-cover rounded-[2.5rem]"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
